import React, { useCallback, ReactNode, Context, useRef } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { uuid } from 'uuidv4';
import { useSelector } from '@xstate/react';
import { State } from 'xstate';
import _isEqual from 'lodash/isEqual';
import S3 from 'aws-sdk/clients/s3';

import { queryStrings } from '@trqk/resolvers';
import {
    getWebpackEnvVar,
    generateAudioFileKeyForS3,
    setInLocalStorage,
    getAudioUploadsLocalStorageKey,
    removeNulls,
} from '@trqk/utils';

import { useTasks, useToast, useTokenModal } from '..';
import { MaxUploadsExceeded } from '../../components/toastMessages/uploadAudio';
import { TASK_GROUP_ID_AUDIO_UPLOADS } from '../../constants';
import { checkAudioFiles } from '../../utils';

const { GET_SESSION, TASK_UPLOAD_AUDIO_FILE_TO_TRQK_AUDIO, GET_AUDIO_UPLOADS } =
    queryStrings;

interface UploadAudioFilesProviderProps {
    children: ReactNode;
    UploadAudioFilesContext: Context<UploadAudioFilesProviderValue>;
}

/**
 * A provider that creates the context for the useUploadAudioFiles hook, which allows the user to
 * upload one or more audio files, each of which is used to create a trqkAudio.
 * @param props - {@link UploadAudioFilesProviderProps}
 * @returns - JSX
 */
export const UploadAudioFilesProvider = ({
    children,
    UploadAudioFilesContext,
}: UploadAudioFilesProviderProps): JSX.Element => {
    const bucketName = getWebpackEnvVar(
        'web',
        'AWS_S3_USER_AUDIO_FILES_BUCKET',
    );
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { sendTaskEvent, taskMachineService } = useTasks();
    const { add: addToast } = useToast();
    const { data: sessionData } = useQuery(GET_SESSION);
    const userId = sessionData?.session?.currentUser?.id;
    const localStorageKey = getAudioUploadsLocalStorageKey(userId);
    const getAudioUploadsQuery = useQuery(GET_AUDIO_UPLOADS, {
        variables: {
            localStorageKey,
        },
    });
    const { cache } = useApolloClient();

    const uploadResults: UploadAudioFileOutput[] =
        getAudioUploadsQuery?.data?.audioUploads ?? [];

    const [taskUploadAudioFileToTrqkAudio] = useMutation(
        TASK_UPLOAD_AUDIO_FILE_TO_TRQK_AUDIO,
        {
            onCompleted: data => {
                const uploadResult =
                    data?.taskUploadAudioFileToTrqkAudio ?? null;

                if (uploadResult !== null) {
                    setInLocalStorage(
                        localStorageKey,
                        (
                            prev: UploadAudioFileOutput[] | null,
                        ): UploadAudioFileOutput[] => {
                            if (prev === null) {
                                return [uploadResult];
                            }

                            return [...prev, uploadResult];
                        },
                    );

                    cache.evict({
                        fieldName: 'audioUploads',
                    });
                }
            },
        },
    );

    // Each upload results in a ManagedUpload instance that we will need access to in order to
    // allow the user to cancel the upload. We use a ref to maintain access to a single object
    // that contains each of the ManagedUpload instances.
    const managedUploads = useRef({} as ManagedUploadsRef);
    // onTaskCancel is bound to the 'CANCEL' event when the task is started. This allows us to
    // call: `sendTaskEvent({ type: 'CANCEL', value: { taskId }})` in order to:
    //  1) notify the task machine to cancel the task, and:
    //  2) cancel the upload with managedUpload.abort()
    const onTaskCancel = (task: UploadAudioTask): void => {
        const managedUpload = managedUploads?.current?.[task.taskId];

        if (managedUpload) {
            managedUpload.abort();
            delete managedUploads.current[task.taskId];

            if (!task.error && !task.done) {
                addToast({
                    content: `Audio upload canceled for: ${task?.input?.filename}`,
                    kind: 'warning-banner',
                });
            }
        }
    };

    const { getToken } = useTokenModal();

    const handleFiles = useCallback(
        ({
            files,
            onGroupStopped,
            maxAudioUploads,
            currentAudioUploads,
            setDetectionTracks,
        }: HandleAudioFilesUploadInput): void => {
            if (files.length > 0 && typeof maxAudioUploads === 'number') {
                const validFiles = checkAudioFiles({ addToast, files });

                if (validFiles !== null) {
                    const audioUploadsLeft = Math.max(
                        0,
                        maxAudioUploads - currentAudioUploads,
                    );

                    if (validFiles.length > audioUploadsLeft) {
                        addToast({
                            RenderContent: MaxUploadsExceeded,
                            renderContentInput: {
                                audioUploadsLeft,
                                maxAudioUploads,
                            },
                            logMessage: 'maxAudioUploadsExceeded',
                            kind: 'warning-card',
                        });
                    } else {
                        const taskCreators = validFiles.map(
                            (file: File): TaskCreator<FilenameInput> => {
                                const taskId = uuid();

                                return {
                                    taskId,
                                    onTaskStart: (
                                        bindOnTaskCancel: BindOnTaskCancel,
                                    ): void => {
                                        bindOnTaskCancel(onTaskCancel);

                                        taskUploadAudioFileToTrqkAudio({
                                            variables: {
                                                bucketName,
                                                bucketRegion,
                                                file,
                                                identityPoolId,
                                                taskId,
                                                trqkDirectoryUrl,
                                                userId,
                                            },
                                            context: {
                                                bindManagedUpload: (
                                                    managedUpload: S3.ManagedUpload,
                                                ): void => {
                                                    if (
                                                        managedUploads?.current
                                                    ) {
                                                        managedUploads.current[
                                                            taskId
                                                        ] = managedUpload;
                                                    }
                                                },
                                                generateS3Key: (
                                                    currentFile: File,
                                                ): string =>
                                                    generateAudioFileKeyForS3({
                                                        file: currentFile,
                                                        userId,
                                                    }),
                                                getToken,
                                                sendTaskEvent,
                                            },
                                        });
                                    },
                                    input: {
                                        filename: file.name,
                                    },
                                };
                            },
                        );

                        sendTaskEvent({
                            type: 'CREATE_GROUP',
                            value: {
                                groupId: TASK_GROUP_ID_AUDIO_UPLOADS,
                                taskCreators,
                                onGroupStopped,
                                onTaskUpdate: (task: UploadAudioTask): void => {
                                    // Upon successful creation of a trqkAudio, update the
                                    // useDetectionTracks hook with the new data
                                    if (task.success) {
                                        const newTrqkAudio: TrqkAudio | null =
                                            task?.output?.trqkAudio ?? null;

                                        if (newTrqkAudio !== null) {
                                            const { id } = newTrqkAudio;

                                            setDetectionTracks(
                                                (
                                                    prev: DetectionTrack[],
                                                ): DetectionTrack[] => [
                                                    ...prev,
                                                    {
                                                        ...newTrqkAudio,
                                                        detection_history:
                                                            removeNulls<DetectionStatusChange>(
                                                                newTrqkAudio.detection_history,
                                                            ),
                                                        audio_id: id,
                                                        title: null,
                                                        work_ids: null,
                                                        percent_shares: null,
                                                    },
                                                ],
                                            );
                                        }
                                    }
                                },
                            },
                        });
                    }
                }
            }
        },
        [addToast, cache, managedUploads.current, userId],
    );

    const compareUploadTasks = (
        prevTasks: UploadAudioTask[],
        nextTasks: UploadAudioTask[],
    ): boolean => _isEqual(prevTasks, nextTasks);

    const taskGroupSelector = (
        state: State<TaskContext, TaskEvent, TaskStateSchema>,
    ): UploadAudioTask[] => {
        if (state?.context?.tasks) {
            return Array.from(state.context.tasks.values()).filter(
                (task: UnknownTask): boolean =>
                    task.groupId === TASK_GROUP_ID_AUDIO_UPLOADS &&
                    !task.canceled, // canceled tasks will still exist until the entire task
                // group stops
            );
        }

        return [];
    };

    const uploadTasks = useSelector(
        taskMachineService,
        taskGroupSelector,
        compareUploadTasks,
    );

    const hasActiveAudioUploads = uploadTasks.reduce(
        (acc: boolean, task: UploadAudioTask): boolean => {
            const canceled = task?.canceled ?? false;
            const done = task?.done ?? false;
            const error = task?.error ?? null;

            if (!canceled && !done && error === null) {
                return true;
            }

            return acc;
        },
        false,
    );

    return (
        <UploadAudioFilesContext.Provider
            value={{
                handleFiles,
                hasActiveAudioUploads,
                uploadTasks,
                uploadResults,
            }}
        >
            {children}
        </UploadAudioFilesContext.Provider>
    );
};
